import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import FloatingPanel from '@/components/ui/FloatingPanel'

const GAP = 8
const VIEWPORT_MARGIN = 16

/**
 * Hover hint carrying the full label of an odds button the column cut off. The buttons
 * hold their width under the pointer, so a title too long for its share is read here
 * instead — and only then: a label that fits raises nothing.
 *
 * One hint per list rather than one per button: only one button is pointed at a time,
 * and a button is drawn in a loop, where a hook of its own could not go. Wear it by
 * spreading the handlers on the button and rendering the node the hook returns.
 *
 * The panel is the shared FloatingPanel, portaled to <body> and positioned from the
 * button's own rect, as the market stats hint is: a row is one of many siblings, and a
 * panel left inside it would be painted over by the next row on scroll.
 */
export const useTruncatedLabelTooltip = () => {
  const anchorRef = useRef(null)
  const panelRef = useRef(null)
  const [label, setLabel] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState(null)

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) return

    const rect = anchor.getBoundingClientRect()
    /* offsetWidth/Height rather than a client rect: the panel animates in with a
       scale/translate, and a rect would measure the transformed, mid-animation box. */
    const panelWidth = panelRef.current?.offsetWidth || 0
    const panelHeight = panelRef.current?.offsetHeight || 0

    /* Above the button, where it covers the row's own line rather than the buttons
       below it — and below when the top of the window leaves no room. */
    const placement = rect.top - panelHeight - GAP < VIEWPORT_MARGIN ? 'below' : 'above'
    const maxLeft = window.innerWidth - panelWidth - VIEWPORT_MARGIN
    const centred = rect.left + rect.width / 2 - panelWidth / 2

    const next = {
      placement,
      left: Math.max(VIEWPORT_MARGIN, Math.min(centred, maxLeft)),
      top: placement === 'above' ? rect.top : rect.bottom,
    }

    /* Same values, same object: the effect below re-measures whenever the position
       changes — the first pass runs before the panel exists and has no width to centre
       against — and bailing out here is what stops that from looping. */
    setPosition(current =>
      current && current.left === next.left && current.top === next.top && current.placement === next.placement
        ? current
        : next
    )
  }, [])

  /* Layout effect: on the first open the panel has not been measured yet, so the
     position taken on hover cannot centre or flip it. Measuring again before paint —
     once the panel it just rendered has a width — keeps the first frame from landing
     off-centre and then jumping into place. It runs on the label too, since moving to
     another button resizes the panel under the same hint. */
  useLayoutEffect(() => {
    if (!isOpen) return
    updatePosition()
  }, [isOpen, label, position, updatePosition])

  useEffect(() => {
    if (!isOpen) return undefined
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen, updatePosition])

  const show = useCallback(event => {
    const button = event.currentTarget
    const labelNode = button?.querySelector?.('[data-truncatable-label]')
    /* `scrollWidth` is the text's full width, `clientWidth` the room it was given. */
    if (!labelNode || labelNode.scrollWidth <= labelNode.clientWidth) return

    anchorRef.current = button
    setLabel(labelNode.textContent)
    setIsOpen(true)
  }, [])

  const hide = useCallback(() => setIsOpen(false), [])

  const labelHintProps = { onMouseEnter: show, onMouseLeave: hide, onFocus: show, onBlur: hide }

  /* Mounted from the first hover on: a list renders dozens of buttons, and a panel per
     button costs a portal each before anyone has asked for one. Once opened it stays
     mounted so the close keeps its exit animation. */
  const labelHint =
    label !== null && position !== null && typeof document !== 'undefined'
      ? createPortal(
          <FloatingPanel
            ref={panelRef}
            isOpen={isOpen}
            className="max-w-[320px] px-3 py-2 text-xs font-medium leading-snug text-Neutral-6"
            style={{
              position: 'fixed',
              zIndex: 9999,
              /* A hint, not a surface to reach: the pointer goes on reaching the row
                 underneath it, so nothing flickers when the panel lands near the cursor. */
              pointerEvents: 'none',
              left: position.left,
              top: position.placement === 'below' ? position.top + GAP : undefined,
              bottom: position.placement === 'above' ? window.innerHeight - position.top + GAP : undefined,
            }}>
            {label}
          </FloatingPanel>,
          document.body
        )
      : null

  return [labelHintProps, labelHint]
}

export default useTruncatedLabelTooltip
